/**
 * Campaign Tag Manager Component
 * 
 * Multi-select tag management with autocomplete for lead campaigns
 * 
 * Requirements: 7.1, 7.2, 7.5, 15.6
 * Task: 25.2
 */

import React, { useState } from 'react';
import {
  Box,
  Chip,
  TextField,
  Autocomplete,
  Typography,
  Stack,
} from '@mui/material';
import { LocalOffer as TagIcon } from '@mui/icons-material';

interface CampaignTagManagerProps {
  /**
   * Current campaign tags assigned to the lead
   */
  tags: string[];
  
  /**
   * Callback when tags are updated
   */
  onChange: (tags: string[]) => void;
  
  /**
   * Available tag suggestions for autocomplete
   * If not provided, allows free-form tag entry
   */
  availableTags?: string[];
  
  /**
   * Label for the input field
   */
  label?: string;
  
  /**
   * Placeholder text when no tags are selected
   */
  placeholder?: string;
  
  /**
   * Whether the component is disabled
   */
  disabled?: boolean;
  
  /**
   * Maximum number of tags allowed
   */
  maxTags?: number;
  
  /**
   * Size variant
   */
  size?: 'small' | 'medium';
}

/**
 * Campaign Tag Manager Component
 * 
 * Features:
 * - Multi-select with autocomplete
 * - Add/remove tags with visual feedback
 * - Display existing tags as chips
 * - Support for predefined tag suggestions
 * - Free-form tag entry when no suggestions provided
 * - Maximum tag limit enforcement
 */
const CampaignTagManager: React.FC<CampaignTagManagerProps> = ({
  tags,
  onChange,
  availableTags = [],
  label = 'Campaign Tags',
  placeholder = 'Add campaign tags...',
  disabled = false,
  maxTags,
  size = 'medium',
}) => {
  const [inputValue, setInputValue] = useState('');

  /**
   * Handle adding a new tag
   */
  const handleAddTag = (newTag: string | null) => {
    if (!newTag) return;
    
    // Trim and validate
    const trimmedTag = newTag.trim();
    if (!trimmedTag) return;
    
    // Check if tag already exists (case-insensitive)
    const tagExists = tags.some(
      (tag) => tag.toLowerCase() === trimmedTag.toLowerCase()
    );
    if (tagExists) return;
    
    // Check max tags limit
    if (maxTags && tags.length >= maxTags) return;
    
    // Add the new tag
    onChange([...tags, trimmedTag]);
    setInputValue('');
  };

  /**
   * Handle removing a tag
   */
  const handleRemoveTag = (tagToRemove: string) => {
    onChange(tags.filter((tag) => tag !== tagToRemove));
  };

  /**
   * Get helper text based on state
   */
  const getHelperText = (): string => {
    if (maxTags && tags.length >= maxTags) {
      return `Maximum ${maxTags} tags allowed`;
    }
    if (tags.length === 0) {
      return 'No campaign tags assigned';
    }
    return `${tags.length} tag${tags.length === 1 ? '' : 's'} assigned`;
  };

  /**
   * Filter available tags to exclude already selected ones
   */
  const getFilteredOptions = (): string[] => {
    if (availableTags.length === 0) return [];
    
    return availableTags.filter(
      (tag) => !tags.some((selectedTag) => 
        selectedTag.toLowerCase() === tag.toLowerCase()
      )
    );
  };

  return (
    <Box>
      <Autocomplete
        multiple={false}
        freeSolo={availableTags.length === 0}
        options={getFilteredOptions()}
        value={null}
        inputValue={inputValue}
        onInputChange={(_, newValue) => setInputValue(newValue)}
        onChange={(_, newValue) => handleAddTag(newValue)}
        disabled={disabled || (maxTags !== undefined && tags.length >= maxTags)}
        size={size}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={tags.length === 0 ? placeholder : ''}
            helperText={getHelperText()}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <TagIcon 
                    sx={{ 
                      ml: 1, 
                      mr: 0.5, 
                      color: 'action.active',
                      fontSize: size === 'small' ? '1rem' : '1.25rem',
                    }} 
                  />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TagIcon fontSize="small" color="action" />
              <Typography variant="body2">{option}</Typography>
            </Stack>
          </li>
        )}
        noOptionsText={
          availableTags.length === 0
            ? 'Type to add a new tag'
            : 'No matching tags'
        }
      />

      {/* Display existing tags */}
      {tags.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={disabled ? undefined : () => handleRemoveTag(tag)}
                color="primary"
                variant="outlined"
                size={size}
                icon={<TagIcon />}
                sx={{
                  borderRadius: 4,
                  fontWeight: 500,
                  mb: 1,
                }}
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default CampaignTagManager;
